import { FlexColumn, FlexRow, StyledButton } from '@gorila-shared-ui/components';
import { DatePicker } from 'baseui/datepicker';
import { OnChangeParams } from 'baseui/select';
import { Filter } from 'baseui/table';
import { memo, useMemo } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { GDL_FILTER_OPTIONS, IVR_FILTER_OPTIONS, ORIGIN_FILTER_OPTIONS } from '../../constants/installations';
import { useStyles } from '../../hooks/useStyles';
import {
  installationCampaignState,
  installationDateRangeState,
  installationFilterGdlStatusState,
  installationFilterJobType,
  installationFilterGnpStatusState,
  installationGnpStatusState,
  installationHdiStatusState,
  installationIvrState,
  installationSearchState,
  tableColumnsState,
  installationFilterHdiStatusState,
} from '../../storage/Installations';
import { JobTypeCategory } from '../../types/job';
import JobTypeCategorySelect from '../shared/selects/JobTypeCategorySelect';
import EnhancedLabeledSelect from '../ui/EnhancedLabeledSelect';

function InstallationFilters() {
  const { theme, css } = useStyles();
  const installationsGnpStatusList = useRecoilValue(installationGnpStatusState);
  const installationsHdiStatusList = useRecoilValue(installationHdiStatusState);
  const [selectGnpStatus, setSelectGnpStatus] = useRecoilState(installationFilterGnpStatusState);
  const [selectGdlStatus, setSelectGdlStatus] = useRecoilState(installationFilterGdlStatusState);
  const [selectHdiStatus, setSelectHdiStatus] = useRecoilState(installationFilterHdiStatusState);
  const [selectJobType, setSelectJobType] = useRecoilState(installationFilterJobType);
  const [selectCampaign, setSelectCampaign] = useRecoilState(installationCampaignState);
  const [ivr, setIvr] = useRecoilState(installationIvrState);
  const [installationDateRange, setInstallationDateRange] = useRecoilState(installationDateRangeState);
  const resetSelectGnpStatus = useResetRecoilState(installationFilterGnpStatusState);
  const resetSelectGdlStatus = useResetRecoilState(installationFilterGdlStatusState);
  const resetSelectHdiStatus = useResetRecoilState(installationFilterHdiStatusState);
  const resetSelectJobType = useResetRecoilState(installationFilterJobType);
  const resetSelectCampaign = useResetRecoilState(installationCampaignState);
  const resetSearch = useResetRecoilState(installationSearchState);
  const resetIvr = useResetRecoilState(installationIvrState);
  const resetSort = useResetRecoilState(tableColumnsState);
  const resetInstallationDateRange = useResetRecoilState(installationDateRangeState);

  const gnpStatusOptions = useMemo(() => {
    if (!installationsGnpStatusList) return;
    return [
      {
        id: '',
        label: 'Todos',
      },
      ...installationsGnpStatusList.map((status) => ({ id: status.clave, label: status.descripcion })),
    ];
  }, [installationsGnpStatusList]);

  const hdiStatusOptions = useMemo(() => {
    if (!installationsHdiStatusList) return;
    return [
      {
        id: '',
        label: 'Todos',
      },
      ...installationsHdiStatusList.map((status) => ({ id: status.clave, label: status.descripcion })),
    ];
  }, [installationsHdiStatusList]);

  const onGdlStatusChange = (params: OnChangeParams) => {
    params.option?.id ? setSelectGdlStatus(params.option.id as string) : setSelectGdlStatus(undefined);
  };

  const onGnpStatusChange = (params: OnChangeParams) => {
    params.option?.id ? setSelectGnpStatus(params.option.id as string) : setSelectGnpStatus('');
  };

  const onHdiStatusChange = (params: OnChangeParams) => {
    params.option?.id ? setSelectHdiStatus(params.option.id as string) : setSelectHdiStatus('');
  };

  const onOriginChange = (params: OnChangeParams) => {
    params.option?.id ? setSelectCampaign(params.option.id as string) : setSelectCampaign(undefined);
  };

  const onIvrChange = (params: OnChangeParams) => {
    params.option?.id ? setIvr(params.option.id as string) : setIvr(undefined);
  };

  const isActive = useMemo(() => {
    if (installationDateRange && installationDateRange[0] && installationDateRange[1]) {
      return true;
    } else if (selectGnpStatus || selectCampaign || ivr) {
      return true;
    }
    return false;
  }, [selectGnpStatus, selectCampaign, ivr, installationDateRange]);

  const resetFilters = () => {
    resetSearch();
    resetSort();
    resetInstallationDateRange();
    resetSelectCampaign();
    resetIvr();
    resetSelectJobType();
    resetSelectGdlStatus();
    resetSelectGnpStatus();
    resetSelectHdiStatus();
  };

  const startDate = installationDateRange && installationDateRange[0] ? new Date(installationDateRange[0]) : undefined;
  const endDate = installationDateRange && installationDateRange[1] ? new Date(installationDateRange[1]) : undefined;

  return (
    <Filter
      active={isActive}
      overrides={{
        Heading: {
          component: () => <></>,
        },
        Footer: {
          component: () => {
            return (
              <FlexRow
                classNames={css({
                  justifyContent: 'end',
                  padding: theme.sizing.scale200,
                })}
              >
                <StyledButton
                  onClick={resetFilters}
                  size="mini"
                  kind="tertiary"
                >
                  Limpiar filtros
                </StyledButton>
              </FlexRow>
            );
          },
        },
        Content: {
          style: () => ({
            borderTop: 'none',
            minHeight: '220px',
          }),
        },
      }}
    >
      <FlexColumn
        classNames={css({
          paddingTop: theme.sizing.scale500,
          paddingBottom: theme.sizing.scale500,
        })}
        gap={theme.sizing.scale200}
      >
        <EnhancedLabeledSelect
          label="Estatus GNP:"
          options={gnpStatusOptions}
          value={[{ id: selectGnpStatus }]}
          onChange={onGnpStatusChange}
          disabled={!installationsGnpStatusList?.length || !!selectGdlStatus || !!selectHdiStatus}
          inline
          inset
        />
        <EnhancedLabeledSelect
          label="Estatus Gorila:"
          options={GDL_FILTER_OPTIONS}
          value={[{ id: selectGdlStatus }]}
          onChange={onGdlStatusChange}
          disabled={!!selectGnpStatus || !!selectHdiStatus}
          inline
          inset
        />
        <EnhancedLabeledSelect
          label="Estatus HDI:"
          options={hdiStatusOptions}
          value={[{ id: selectHdiStatus }]}
          onChange={onHdiStatusChange}
          disabled={!installationsHdiStatusList?.length || !!selectGnpStatus || !!selectGdlStatus}
          inline
          inset
        />
        <JobTypeCategorySelect
          label="Categoría"
          selectedId={selectJobType as JobTypeCategory}
          onChangeId={setSelectJobType}
          inline
          isFilter
          showEmptyOption
        />
        <EnhancedLabeledSelect
          label="Origen:"
          options={ORIGIN_FILTER_OPTIONS}
          value={[{ id: selectCampaign }]}
          onChange={onOriginChange}
          inline
          inset
        />
        <EnhancedLabeledSelect
          label="IVR:"
          options={IVR_FILTER_OPTIONS}
          value={[{ id: ivr }]}
          onChange={onIvrChange}
          inline
          inset
        />
        <DatePicker
          placeholder="Fecha inicio - Fecha fin"
          maxDate={new Date()}
          range
          size="compact"
          value={[startDate, endDate]}
          onChange={({ date: dates }) => {
            if (dates instanceof Array) {
              if (dates[0] && dates[1]) {
                const endDate = dates[1];
                endDate.setHours(23, 59, 59);
                setInstallationDateRange([dates[0].getTime(), endDate.getTime()]);
              } else if (dates[0]) {
                setInstallationDateRange([dates[0].getTime(), null]);
              }
            }
          }}
        />
      </FlexColumn>
    </Filter>
  );
}

export default memo(InstallationFilters);
